const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MILLISECONDS_PER_DAY = 86_400_000;

export const FIRST_DAY = 1;

export type DateInput = Date | number | string;

function toValidDate(value: DateInput): Date {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    throw new RangeError("Expected a valid date or timestamp.");
  }
  return date;
}

function isoDateToEpochDay(value: string): number {
  const match = ISO_DATE_PATTERN.exec(value);
  if (!match) {
    throw new RangeError(`Expected YYYY-MM-DD, received "${value}".`);
  }

  const timestamp = Date.parse(`${value}T00:00:00.000Z`);
  if (
    Number.isNaN(timestamp) ||
    new Date(timestamp).toISOString().slice(0, 10) !== value
  ) {
    throw new RangeError(`Expected a real calendar date, received "${value}".`);
  }

  return timestamp / MILLISECONDS_PER_DAY;
}

/**
 * Returns the Gregorian calendar date at an instant in an IANA time zone.
 *
 * `formatToParts` is used instead of relying on a locale-specific formatted
 * string, so the result is always `YYYY-MM-DD`.
 */
export function getDateInTimeZone(date: DateInput, timezone: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    calendar: "gregory",
    numberingSystem: "latn",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(toValidDate(date));

  const values = Object.fromEntries(
    parts
      .filter(
        (part) =>
          part.type === "year" || part.type === "month" || part.type === "day",
      )
      .map((part) => [part.type, part.value]),
  );

  return `${values.year}-${values.month}-${values.day}`;
}

/**
 * Counts calendar boundaries, not elapsed 24-hour periods.
 *
 * The inputs are already local calendar dates, so interpreting their
 * year/month/day components at UTC midnight avoids daylight-saving offsets.
 */
export function calendarDayDifference(
  startDate: string,
  endDate: string,
): number {
  return isoDateToEpochDay(endDate) - isoDateToEpochDay(startDate);
}

/**
 * Today's 1-based day, unbounded above so callers can tell "finished" from
 * "still going". Dates before the start clamp to day 1.
 */
export function getElapsedDay(
  startDate: string,
  timezone: string,
  now: DateInput = new Date(),
): number {
  const today = getDateInTimeZone(now, timezone);
  return Math.max(FIRST_DAY, calendarDayDifference(startDate, today) + 1);
}

/** Today's day, clamped to the journey the current word count produces. */
export function getCurrentDay(
  startDate: string,
  timezone: string,
  totalDays: number,
  now: DateInput = new Date(),
): number {
  return Math.min(
    Math.max(FIRST_DAY, totalDays),
    getElapsedDay(startDate, timezone, now),
  );
}

/** The calendar date a journey day falls on, as `YYYY-MM-DD`. */
export function getDateForDay(startDate: string, day: number): string {
  const epochDay =
    isoDateToEpochDay(startDate) + (Math.max(FIRST_DAY, day) - FIRST_DAY);
  return new Date(epochDay * MILLISECONDS_PER_DAY).toISOString().slice(0, 10);
}

/**
 * Midday keeps the label on the intended date whatever the formatter does at
 * the edges of a day.
 */
function noonUtc(isoDate: string): Date {
  return new Date(`${isoDate}T12:00:00.000Z`);
}

/** "Sunday 16 August": the date one sheet belongs to. */
export function getSheetDateLabel(startDate: string, day: number): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(noonUtc(getDateForDay(startDate, day)));
}

/** "Mon", "Tue": the weekday a day of the journey lands on. */
export function getWeekdayLabel(startDate: string, day: number): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    weekday: "short",
  }).format(noonUtc(getDateForDay(startDate, day)));
}

/** "16 to 22 August": the span a week of sheets covers. */
export function getDateRangeLabel(
  startDate: string,
  fromDay: number,
  toDay: number,
): string {
  const from = noonUtc(getDateForDay(startDate, fromDay));
  const to = noonUtc(getDateForDay(startDate, toDay));
  const dayOnly = new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    day: "numeric",
  });
  const dayAndMonth = new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    day: "numeric",
    month: "long",
  });

  if (from.getTime() === to.getTime()) return dayAndMonth.format(from);

  const sameMonth =
    from.getUTCFullYear() === to.getUTCFullYear() &&
    from.getUTCMonth() === to.getUTCMonth();

  return `${sameMonth ? dayOnly.format(from) : dayAndMonth.format(from)} to ${dayAndMonth.format(to)}`;
}

export function hasFinishedJourney(
  startDate: string,
  timezone: string,
  totalDays: number,
  now: DateInput = new Date(),
): boolean {
  return getElapsedDay(startDate, timezone, now) > totalDays;
}
